import { Progress } from "./progress";
import { usePage } from "@tree-company/tree-query";
import clsx from "clsx";
import { useEffect, useMemo, useRef } from "react";
import Markdown from "react-markdown";
import { STATEMENTS } from "../data/statements";
import translate from "../locales";

function stepForPageKey(key) {
  if (key.startsWith("opinion")) {
    return 1;
  } else if (key.startsWith("comment")) {
    return 2;
  } else if (key === "name") {
    return 3;
  } else if (key === "organisation") {
    return 4;
  } else if (key === "like") {
    return 5;
  } else if (key === "optin") {
    return 6;
  }
}

export function PageComponent({
  handleSave,
  page,
  children,
  onPreviousPage,
  className,
}) {
  const { title, description, htmlAttributes, key } = usePage(page);
  const step = useMemo(() => stepForPageKey(key), [key]);
  const statementName = useMemo(() => {
    const name = page.treesponse.getAnswer("statement");
    return STATEMENTS[name] ? name : null;
  }, [key]);
  const showMoreInfo = statementName && key.startsWith("opinion");
  const moreInfoDialogRef = useRef(null);

  const openMoreInfo = () => {
    moreInfoDialogRef.current?.showModal();
    if (window.fathom) {
      window.fathom.trackEvent("pop-up KB opened");
    }
  };

  const closeMoreInfo = () => {
    moreInfoDialogRef.current?.close();
  };

  // Light-dismiss: close when clicking the backdrop. Padding on the dialog
  // means we can't use `e.target === dialog`; check the bounding rect instead.
  const onDialogBackdropClick = (event) => {
    const dialog = moreInfoDialogRef.current;
    if (!dialog) return;
    const { top, bottom, left, right } = dialog.getBoundingClientRect();
    const insideDialog =
      event.clientY >= top &&
      event.clientY <= bottom &&
      event.clientX >= left &&
      event.clientX <= right;
    if (!insideDialog) dialog.close();
  };
  // On the first page there is no in-survey previous page, so fall back to
  // browser history to return to wherever the user came from (statement
  // selection page, or the "Next" page of the previously completed statement).
  const onBackButton =
    step === 1 ? () => window.history.back() : onPreviousPage;

  // Make sure the browsers back button has the same effect as our back button
  useEffect(() => {
    const goBack = (event) => {
      event.preventDefault();
      onPreviousPage();
    };
    window.addEventListener("popstate", goBack, { once: true });
    return () => window.removeEventListener("popstate", goBack, { once: true });
  }, [key]);

  useEffect(() => {
    // If our context has defined fathom, we send fake pages with the current step of the form
    if (window.fathom) {
      const locationWithKey = new URL(window.location);
      locationWithKey.pathname += `/${key}`;
      window.fathom.trackPageview({ url: locationWithKey.toString() });
    }
  }, [key]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, [key]);

  return (
    <main className={clsx("app__container", className)}>
      <div className="card">
        <form
          action=""
          className={clsx("survey", htmlAttributes.class)}
          onSubmit={handleSave}
        >
          <Progress
            title={
              statementName
                ? translate(["statements", statementName, "shortName"])
                : undefined
            }
            onBackButton={onBackButton}
            step={step}
          />
          {(title || description || showMoreInfo) && (
            <div className="survey__intro">
              {title && <h1 className="survey__title">{title}</h1>}
              {showMoreInfo && (
                <button
                  type="button"
                  className="link link--center"
                  onClick={openMoreInfo}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="link__icon"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                  >
                    <path
                      d="M6 10H7.33333V6H6V10ZM7.14167 4.475C7.26944 4.34722 7.33333 4.18889 7.33333 4C7.33333 3.81111 7.26944 3.65278 7.14167 3.525C7.01389 3.39722 6.85556 3.33333 6.66667 3.33333C6.47778 3.33333 6.31944 3.39722 6.19167 3.525C6.06389 3.65278 6 3.81111 6 4C6 4.18889 6.06389 4.34722 6.19167 4.475C6.31944 4.60278 6.47778 4.66667 6.66667 4.66667C6.85556 4.66667 7.01389 4.60278 7.14167 4.475ZM6.66667 13.3333C5.74444 13.3333 4.87778 13.1583 4.06667 12.8083C3.25556 12.4583 2.55 11.9833 1.95 11.3833C1.35 10.7833 0.875 10.0778 0.525 9.26667C0.175 8.45555 0 7.58889 0 6.66667C0 5.74444 0.175 4.87778 0.525 4.06667C0.875 3.25556 1.35 2.55 1.95 1.95C2.55 1.35 3.25556 0.875 4.06667 0.525C4.87778 0.175 5.74444 0 6.66667 0C7.58889 0 8.45555 0.175 9.26667 0.525C10.0778 0.875 10.7833 1.35 11.3833 1.95C11.9833 2.55 12.4583 3.25556 12.8083 4.06667C13.1583 4.87778 13.3333 5.74444 13.3333 6.66667C13.3333 7.58889 13.1583 8.45555 12.8083 9.26667C12.4583 10.0778 11.9833 10.7833 11.3833 11.3833C10.7833 11.9833 10.0778 12.4583 9.26667 12.8083C8.45555 13.1583 7.58889 13.3333 6.66667 13.3333ZM6.66667 12C8.15555 12 9.41667 11.4833 10.45 10.45C11.4833 9.41667 12 8.15555 12 6.66667C12 5.17778 11.4833 3.91667 10.45 2.88333C9.41667 1.85 8.15555 1.33333 6.66667 1.33333C5.17778 1.33333 3.91667 1.85 2.88333 2.88333C1.85 3.91667 1.33333 5.17778 1.33333 6.66667C1.33333 8.15555 1.85 9.41667 2.88333 10.45C3.91667 11.4833 5.17778 12 6.66667 12Z"
                      fill="currentcolor"
                    />
                  </svg>
                  {translate([
                    "statements",
                    statementName,
                    "more_info",
                    "label",
                  ])}
                </button>
              )}
              {description && <p className="survey__text">{description}</p>}
            </div>
          )}

          {children}

          {page.settings.showNext !== false && (
            <button className="survey__action">
              {translate("general.next_page")}
            </button>
          )}
        </form>
      </div>

      {showMoreInfo && (
        <dialog
          ref={moreInfoDialogRef}
          aria-labelledby={`moreInfoDialogTitle-${statementName}`}
          className="dialog"
          onClick={onDialogBackdropClick}
        >
          <div className="dialog__content">
            <div className="dialog__header">
              <h2
                id={`moreInfoDialogTitle-${statementName}`}
                className="dialog__title"
              >
                {translate(["statements", statementName, "more_info", "title"])}
              </h2>
              <button
                type="button"
                className="dialog__close"
                onClick={closeMoreInfo}
              >
                <span className="sr-only">{translate("general.close")}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentcolor"
                  className="dialog__close-icon"
                >
                  <path d="M5.33335 15.8334L4.16669 14.6667L8.83335 10L4.16669 5.33335L5.33335 4.16669L10 8.83335L14.6667 4.16669L15.8334 5.33335L11.1667 10L15.8334 14.6667L14.6667 15.8334L10 11.1667L5.33335 15.8334Z" />
                </svg>
              </button>
            </div>
            <p className="dialog__subtitle">
              {translate([
                "statements",
                statementName,
                "more_info",
                "subtitle",
              ])}
            </p>
            <div className="dialog__body">
              <Markdown>
                {translate([
                  "statements",
                  statementName,
                  "more_info",
                  "description",
                ])}
              </Markdown>
            </div>
          </div>
        </dialog>
      )}
    </main>
  );
}
